// components/Chat/QuickCreateAgent.jsx
//
// "Quick create → Agent" — a short conditional wizard (popover, like the avatar
// one) that gathers the essentials of an agent and hands them to Zeno as a
// ```zeno:card {type:"agent"}``` block. Zeno then creates it with its create_agent
// tool (resolving the real model / skills / avatar via list_* first).
//
// An agent = a runnable assistant, optionally built from an avatar (behavior),
// a model (intelligence), skills (tools/integrations), and a project.
import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Headset,
  MessageSquare,
} from 'lucide-react';
import TemplateService from '../../services/template.service';
import AssistantService from '../../services/assistant.service';
import { buildZenoCardBlock } from './zenoCards';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useProjects } from '../../hooks/useOrganizations';

// Intelligence presets — the user picks a tier in plain language; Zeno maps it to
// a real model via list_models. "auto" lets Zeno choose.
const INTELLIGENCE = [
  { key: 'auto', label: 'Let Zeno choose', hint: 'recommended' },
  { key: 'basic', label: 'Basic', hint: 'fast & cheap' },
  { key: 'balanced', label: 'Balanced', hint: 'good default' },
  { key: 'advanced', label: 'Advanced', hint: 'smarter' },
  { key: 'genius', label: 'Genius', hint: 'most capable' },
];

const EMPTY = {
  name: '',
  description: '',
  avatar: '', // base avatar (template) id, or '' = start fresh
  intelligence: 'auto',
  skills: [], // selected skill names
  isPublic: false,
  projectId: null,
};

export default function QuickCreateAgent({ open, onClose, accountId, onSendToChat }) {
  const { currentAccount } = useOrganization();
  const { projects } = useProjects();
  const projectList = Array.isArray(projects) ? projects : [];
  const resolvedAccountId = accountId || currentAccount?.id;

  const [a, setA] = useState(EMPTY);
  const [stepIdx, setStepIdx] = useState(0);
  const [avatars, setAvatars] = useState([]);
  const [skills, setSkills] = useState([]);

  const set = (key) => (v) => setA((prev) => ({ ...prev, [key]: v }));

  // Load the account's avatars + the platform skills to offer as options.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (resolvedAccountId) {
        const r = await TemplateService.listAccountTemplates(resolvedAccountId, {
          limit: 50,
          sort_by: 'updated_at',
          descending: true,
          view: 'summary',
        }).catch(() => null);
        const arr = Array.isArray(r?.data) ? r.data : r?.data?.templates || [];
        if (!cancelled) setAvatars(arr);
      }
      const s = await AssistantService.getSkills().catch(() => null);
      const list = s?.data?.skills || s?.data || [];
      if (!cancelled) setSkills(Array.isArray(list) ? list : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, resolvedAccountId]);

  const steps = useMemo(
    () => ['name', 'description', 'avatar', 'intelligence', 'skills', 'visibility', 'review'],
    []
  );

  if (!open) return null;

  const step = steps[Math.min(stepIdx, steps.length - 1)];
  const isLast = step === 'review';

  const canNext = (() => {
    switch (step) {
      case 'name':
        return a.name.trim().length > 0;
      case 'description':
        return a.description.trim().length > 0;
      case 'visibility':
        return a.isPublic || !!a.projectId;
      default:
        return true;
    }
  })();

  const next = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const skillName = (s) => (typeof s === 'string' ? s : s.name || s.id);

  const handleSendToChat = () => {
    const baseAvatar = avatars.find((t) => t.id === a.avatar);
    const project = projectList.find((p) => p.id === a.projectId);
    const card = {
      type: 'agent',
      name: a.name.trim(),
      description: a.description.trim() || undefined,
      model: a.intelligence === 'auto' ? undefined : a.intelligence,
      avatar: baseAvatar ? baseAvatar.name : undefined,
      skills: a.skills.length ? a.skills : undefined,
      is_public: a.isPublic,
      project: !a.isPublic && project ? project.name : undefined,
      status: 'draft',
    };
    const intro =
      `Create this agent for me. Pick a real model for the "${a.intelligence}" ` +
      `intelligence with list_models, resolve the avatar and skills with list_avatars/` +
      `list_skills, confirm, then create it.`;
    onSendToChat?.(`${intro}\n\n${buildZenoCardBlock(card)}`);
    onClose?.();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[#8844F5]/30 bg-[#141018]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <Headset className="h-4 w-4 text-[#b98bff]" />
          New agent
        </span>
        <button
          onClick={onClose}
          title="Collapse"
          className="rounded-full p-1.5 text-gray-400 hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress */}
      <div className="flex gap-1 px-4 pt-3">
        {steps.map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${i <= stepIdx ? 'bg-[#8844F5]' : 'bg-white/10'}`}
          />
        ))}
      </div>

      <div className="px-4 py-5 min-h-[160px]">
        {step === 'name' && (
          <Field label="What should the agent be called?">
            <TextInput
              autoFocus
              value={a.name}
              onChange={set('name')}
              placeholder="e.g. Support Agent, Sales Bot…"
            />
          </Field>
        )}

        {step === 'description' && (
          <Field label="What should it do?" hint="One or two lines about its job.">
            <TextArea
              value={a.description}
              onChange={set('description')}
              placeholder="e.g. Answer WhatsApp FAQs about our plans and escalate to a human when needed."
            />
          </Field>
        )}

        {step === 'avatar' && (
          <Field
            label="Base it on an avatar?"
            hint="An avatar defines the behavior. Optional — you can start fresh."
          >
            <div className="flex flex-wrap gap-2">
              <Chip active={!a.avatar} onClick={() => set('avatar')('')}>
                Start fresh
              </Chip>
              {avatars.map((t) => (
                <Chip key={t.id} active={a.avatar === t.id} onClick={() => set('avatar')(t.id)}>
                  {t.name || 'Untitled'}
                </Chip>
              ))}
            </div>
          </Field>
        )}

        {step === 'intelligence' && (
          <Field label="How smart should it be?" hint="Higher tiers cost more coins per task.">
            <div className="flex flex-wrap gap-2">
              {INTELLIGENCE.map((m) => (
                <Chip key={m.key} active={a.intelligence === m.key} onClick={() => set('intelligence')(m.key)}>
                  {m.label} <span className="text-gray-500">· {m.hint}</span>
                </Chip>
              ))}
            </div>
          </Field>
        )}

        {step === 'skills' && (
          <Field
            label="Skills / tools (optional)"
            hint="What it can use — web search, integrations, databases…"
          >
            {skills.length === 0 ? (
              <p className="text-xs text-gray-500">
                No skills to pick — Zeno can suggest and wire them up in the chat.
              </p>
            ) : (
              <div className="flex max-h-[180px] flex-wrap gap-2 overflow-y-auto">
                {skills.map((s) => {
                  const name = skillName(s);
                  return (
                    <Chip
                      key={name}
                      active={a.skills.includes(name)}
                      onClick={() =>
                        set('skills')(
                          a.skills.includes(name)
                            ? a.skills.filter((k) => k !== name)
                            : [...a.skills, name]
                        )
                      }
                    >
                      {name}
                    </Chip>
                  );
                })}
              </div>
            )}
          </Field>
        )}

        {step === 'visibility' && (
          <Field
            label="Who can use this agent?"
            hint="Public = account-wide. Private = inside one project."
          >
            <div className="flex gap-2">
              <Chip
                active={a.isPublic === true}
                onClick={() => setA((p) => ({ ...p, isPublic: true, projectId: null }))}
              >
                Public
              </Chip>
              <Chip active={a.isPublic === false} onClick={() => set('isPublic')(false)}>
                Private
              </Chip>
            </div>
            {a.isPublic === false && (
              <div className="mt-4">
                <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Project
                </label>
                {projectList.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    No projects available — create one first, or make it public.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {projectList.map((p) => (
                      <Chip key={p.id} active={a.projectId === p.id} onClick={() => set('projectId')(p.id)}>
                        {p.name || 'Untitled'}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Field>
        )}

        {step === 'review' && (
          <div>
            <p className="mb-3 text-sm font-medium text-white">Review your agent</p>
            <div className="space-y-1.5 text-sm text-gray-300">
              <Row k="Name" v={a.name} />
              <Row k="Does" v={a.description} />
              <Row k="Avatar" v={avatars.find((t) => t.id === a.avatar)?.name || 'Start fresh'} />
              <Row k="Intelligence" v={INTELLIGENCE.find((m) => m.key === a.intelligence)?.label} />
              <Row k="Skills" v={a.skills.length ? a.skills.join(', ') : 'None yet'} />
              <Row
                k="Visibility"
                v={a.isPublic ? 'Public' : `Private · ${projectList.find((p) => p.id === a.projectId)?.name || '—'}`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
        <button
          onClick={back}
          disabled={stepIdx === 0}
          className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-300 hover:bg-white/5 disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {!isLast ? (
          <button
            onClick={next}
            disabled={!canNext}
            className="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #8844F5, #6e27e0)' }}
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleSendToChat}
            className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white"
            style={{ background: 'linear-gradient(135deg, #8844F5, #6e27e0)' }}
          >
            <MessageSquare className="h-4 w-4" /> Send to chat
          </button>
        )}
      </div>
    </div>
  );
}

// ---- small presentational helpers ----------------------------------------

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-3 block text-base font-medium text-white">{label}</label>
      {hint && <p className="-mt-2 mb-3 text-xs text-gray-500">{hint}</p>}
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 flex-shrink-0 text-gray-500">{k}</span>
      <span className="min-w-0 flex-1 text-gray-200">{v || '—'}</span>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, autoFocus }) {
  return (
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none placeholder:text-gray-500 focus:border-[#8844F5]/60"
    />
  );
}

function TextArea({ value, onChange, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full resize-y rounded-lg border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none placeholder:text-gray-500 focus:border-[#8844F5]/60"
    />
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${
        active
          ? 'border-[#8844F5] bg-[#8844F5]/15 text-white'
          : 'border-white/12 bg-white/[0.03] text-gray-300 hover:border-[#8844F5]/40'
      }`}
    >
      {children}
    </button>
  );
}
